import type { HiringRecommendation, InterviewSummary, KpiGrade, Meeting } from "@scoach/types";
import { langMeta } from "@scoach/types";
import { useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";

import { DictateButton } from "../../../components/DictateButton.tsx";
import { ReadAloudButton } from "../../../components/ReadAloudButton.tsx";
import { ApiError, buildAuthHeaders } from "../../../lib/http.ts";
import { useAuthStore } from "../../auth/store.ts";
import { DashHeader } from "../../dashboard/components/DashHeader.tsx";
import { FullTranscript } from "../../summary/components/FullTranscript.tsx";
import { interviewApi } from "../api.ts";

function Shell({ onHome: _onHome, children }: { onHome?: () => void; children: React.ReactNode }) {
  return (
    <div className="dash">
      <DashHeader />
      {children}
    </div>
  );
}

const API_BASE =
  import.meta.env.VITE_API_BASE_URL === undefined
    ? "http://localhost:8080"
    : import.meta.env.VITE_API_BASE_URL;

const GRADES: KpiGrade[] = ["No rating", "Poor", "Borderline", "Solid", "Outstanding"];
const RECS: HiringRecommendation[] = [
  "Strong hire",
  "Hire",
  "Lean hire",
  "Lean no hire",
  "No hire",
  "Strong no hire",
  "Not enough info",
];

const GRADE_COLOR: Record<KpiGrade, string> = {
  "No rating": "var(--text-4)",
  Poor: "#ea4335",
  Borderline: "#f9ab00",
  Solid: "#1a73e8",
  Outstanding: "#34a853",
};

export function InterviewSummaryScreen({ meetingId }: { meetingId: string }) {
  const nav = useNavigate();
  const firebaseUid = useAuthStore((s) => s.firebaseUid);
  const [summary, setSummary] = useState<InterviewSummary | null>(null);
  const [status, setStatus] = useState<"loading" | "not-ready" | "ready" | "error">("loading");
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  // Only the interviewer who ran the interview may edit the evaluation. Admins
  // opening it from the dashboard drill-down get a read-only view (the API also
  // enforces this — edits are owner-only). Defaults to editable so the owner is
  // never blocked if the meeting lookup is slow/fails.
  const [canEdit, setCanEdit] = useState(true);
  const [meeting, setMeeting] = useState<Meeting | null>(null);
  // Which candidate-detail viewer is expanded (résumé / notes / transcript).
  const [detailView, setDetailView] = useState<"resume" | "notes" | "transcript" | null>(null);

  // Resolve ownership to decide whether editing controls are shown, and keep the
  // meeting around for the candidate-detail viewers (résumé / notes / transcript).
  useEffect(() => {
    let cancelled = false;
    interviewApi
      .fetchMeeting(meetingId)
      .then((m) => {
        if (cancelled) return;
        setMeeting(m);
        setCanEdit(!firebaseUid || m.ownerUid === firebaseUid);
      })
      .catch(() => {
        /* leave editable — the owner path shouldn't be penalized on a lookup error */
      });
    return () => {
      cancelled = true;
    };
  }, [meetingId, firebaseUid]);

  // Poll for the evaluation (generated in the background on end).
  useEffect(() => {
    let cancelled = false;
    let tries = 0;
    const load = async () => {
      try {
        const s = await interviewApi.fetchSummary(meetingId);
        if (cancelled) return;
        setSummary(s);
        setSubmitted(Boolean(s.submittedAt));
        setStatus("ready");
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 404 && tries < 20) {
          tries++;
          setStatus("not-ready");
          setTimeout(load, 6000);
        } else {
          setStatus("error");
        }
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [meetingId]);

  const update = useCallback((patch: Partial<InterviewSummary>) => {
    setSummary((prev) => (prev ? { ...prev, ...patch } : prev));
  }, []);

  // Open the stored original résumé file (authenticated) in a new tab.
  const openResumeFile = useCallback(async () => {
    try {
      const headers = await buildAuthHeaders();
      const res = await fetch(`${API_BASE}/interviews/${meetingId}/cv/file`, { headers });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const url = URL.createObjectURL(await res.blob());
      window.open(url, "_blank", "noopener");
      // Revoke a bit later so the new tab has time to load the blob.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      alert(`Could not open résumé: ${(err as Error).message}`);
    }
  }, [meetingId]);

  async function save() {
    if (!summary) return;
    setSaving(true);
    try {
      const saved = await interviewApi.saveSummary(meetingId, summary);
      setSummary(saved);
    } catch (err) {
      alert(`Save failed: ${(err as Error).message}`);
    } finally {
      setSaving(false);
    }
  }

  async function submit() {
    await save();
    try {
      const s = await interviewApi.submitSummary(meetingId);
      setSummary(s);
      setSubmitted(true);
    } catch (err) {
      alert(`Submit failed: ${(err as Error).message}`);
    }
  }

  if (status === "loading" || status === "not-ready") {
    return (
      <Shell onHome={() => nav({ to: "/interviews" })}>
        <div style={{ maxWidth: 820, margin: "40px auto", textAlign: "center" }}>
          <h2>Generating evaluation…</h2>
          <p style={{ color: "var(--text-3)" }}>
            The AI is scoring the interview against the scorecard. This can take up to a few
            minutes.
          </p>
        </div>
      </Shell>
    );
  }
  if (status === "error" || !summary) {
    return (
      <Shell onHome={() => nav({ to: "/interviews" })}>
        <div style={{ maxWidth: 640, margin: "40px auto", textAlign: "center" }}>
          <h2>Evaluation not available</h2>
          <p style={{ color: "var(--text-3)" }}>
            This usually means no speech was transcribed for this interview — check that your
            microphone and the candidate's shared audio (tab/window) were enabled during the call.
            If you did speak, the evaluation may still be processing; give it a minute and retry.
          </p>
          <div style={{ display: "flex", gap: 8, justifyContent: "center", marginTop: 16 }}>
            <button type="button" className="pill-btn" onClick={() => window.location.reload()}>
              Retry
            </button>
            <button
              type="button"
              className="pill-btn primary"
              onClick={() => nav({ to: "/interviews" })}
            >
              Back to interviews
            </button>
          </div>
        </div>
      </Shell>
    );
  }

  const grade = (k: (typeof summary.kpis)[number]) => k.editedGrade ?? k.grade;

  // The prose read aloud: recommendation + justification + KPI grades.
  const summaryText = () => {
    const parts = [
      `Hiring recommendation: ${summary.recommendation.value}.`,
      summary.recommendation.justification,
      ...summary.kpis.map((k) => `${k.kpiName}: ${grade(k)}.`),
    ];
    return parts.filter(Boolean).join(" ");
  };

  return (
    <Shell onHome={() => nav({ to: "/interviews" })}>
      <div style={{ maxWidth: 900, margin: "0 auto", padding: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h1 style={{ marginBottom: 0 }}>Interview evaluation</h1>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <ReadAloudButton
              getText={summaryText}
              lang={meeting?.language && meeting.language !== "auto" ? meeting.language : undefined}
            />
            {canEdit ? (
              <>
                <button
                  type="button"
                  className="pill-btn"
                  onClick={save}
                  disabled={saving || submitted}
                >
                  {saving ? "Saving…" : "Save"}
                </button>
                <button
                  type="button"
                  className="pill-btn primary"
                  onClick={submit}
                  disabled={submitted}
                >
                  {submitted ? "Submitted ✓" : "Submit"}
                </button>
              </>
            ) : (
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: "var(--text-3)",
                  border: "1px solid var(--border-1)",
                  borderRadius: 999,
                  padding: "4px 12px",
                }}
              >
                Read-only (admin view)
              </span>
            )}
          </div>
        </div>
        <p style={{ color: "var(--text-3)", marginTop: 4 }}>
          Candidate is referred to as <strong>TC</strong>.{" "}
          {canEdit
            ? "All fields are editable before submitting."
            : "You are viewing another interviewer's evaluation (read-only)."}
        </p>

        {/* Candidate details — résumé, interviewer notes, full transcript */}
        <section style={card}>
          <h3 style={{ marginTop: 0 }}>Candidate details</h3>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button
              type="button"
              className={detailView === "resume" ? "pill-btn primary" : "pill-btn"}
              onClick={() => setDetailView((v) => (v === "resume" ? null : "resume"))}
            >
              {detailView === "resume" ? "Hide résumé" : "View résumé"}
            </button>
            <button
              type="button"
              className={detailView === "notes" ? "pill-btn primary" : "pill-btn"}
              onClick={() => setDetailView((v) => (v === "notes" ? null : "notes"))}
            >
              {detailView === "notes" ? "Hide notes" : "View my notes"}
            </button>
            <button
              type="button"
              className={detailView === "transcript" ? "pill-btn primary" : "pill-btn"}
              onClick={() => setDetailView((v) => (v === "transcript" ? null : "transcript"))}
            >
              {detailView === "transcript" ? "Hide transcript" : "View full transcript"}
            </button>
          </div>

          {detailView === "resume" && (
            <div style={{ marginTop: 14 }}>
              {meeting?.interview?.cvContext || meeting?.interview?.cvGcsPath ? (
                <>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      marginBottom: 8,
                      flexWrap: "wrap",
                    }}
                  >
                    {meeting?.interview?.cvFileName && (
                      <span style={{ fontSize: 12, color: "var(--text-3)" }}>
                        {meeting.interview.cvFileName}
                      </span>
                    )}
                    {meeting?.interview?.cvGcsPath && (
                      <button type="button" className="pill-btn" onClick={openResumeFile}>
                        Open original file
                      </button>
                    )}
                  </div>
                  {meeting?.interview?.cvContext && (
                    <pre
                      style={{
                        whiteSpace: "pre-wrap",
                        fontFamily: "inherit",
                        fontSize: 13,
                        color: "var(--text-2)",
                        background: "var(--surface-2)",
                        border: "1px solid var(--border-1)",
                        borderRadius: 8,
                        padding: 12,
                        maxHeight: 360,
                        overflowY: "auto",
                        margin: 0,
                      }}
                    >
                      {meeting.interview.cvContext}
                    </pre>
                  )}
                </>
              ) : (
                <p style={{ color: "var(--text-3)", fontSize: 13 }}>
                  No résumé was uploaded for this candidate.
                </p>
              )}
            </div>
          )}

          {detailView === "notes" && (
            <div style={{ marginTop: 14 }}>
              {meeting?.notes && meeting.notes.length > 0 ? (
                meeting.notes.map((n, i) => (
                  <div key={i} style={{ fontSize: 14, padding: "3px 0" }}>
                    <span
                      style={{
                        color: "var(--text-4)",
                        fontSize: 12,
                        fontFamily: "var(--font-mono)",
                      }}
                    >
                      {n.t}
                    </span>{" "}
                    {n.text}
                    {n.source === "auto" && (
                      <em style={{ marginLeft: 6, fontSize: 10, color: "var(--text-4)" }}>auto</em>
                    )}
                  </div>
                ))
              ) : (
                <p style={{ color: "var(--text-3)", fontSize: 13 }}>
                  No personal notes were captured during this interview.
                </p>
              )}
            </div>
          )}

          {detailView === "transcript" && (
            <div style={{ marginTop: 14 }}>
              <FullTranscript meetingId={meetingId} />
            </div>
          )}
        </section>

        {/* Recommendation */}
        <section style={card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h3 style={{ marginTop: 0, marginBottom: 0 }}>Hiring recommendation</h3>
            {canEdit && !submitted && (
              <DictateButton
                label="Dictate justification"
                lang={
                  meeting?.language && meeting.language !== "auto"
                    ? langMeta(meeting.language).bcp47
                    : undefined
                }
                onText={(t) =>
                  setSummary((prev) =>
                    prev
                      ? {
                          ...prev,
                          recommendation: {
                            ...prev.recommendation,
                            justification: `${prev.recommendation.justification}${
                              prev.recommendation.justification ? " " : ""
                            }${t}`,
                          },
                        }
                      : prev,
                  )
                }
              />
            )}
          </div>
          <select
            style={input}
            value={summary.recommendation.value}
            disabled={submitted || !canEdit}
            onChange={(e) =>
              update({
                recommendation: {
                  ...summary.recommendation,
                  value: e.target.value as HiringRecommendation,
                },
              })
            }
          >
            {RECS.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
          <textarea
            style={{ ...input, width: "100%", minHeight: 70, marginTop: 10 }}
            value={summary.recommendation.justification}
            disabled={submitted || !canEdit}
            onChange={(e) =>
              update({
                recommendation: { ...summary.recommendation, justification: e.target.value },
              })
            }
          />
        </section>

        {/* KPI matrix */}
        <section style={card}>
          <h3 style={{ marginTop: 0 }}>KPI evaluation</h3>
          {summary.kpis.length === 0 && (
            <p style={{ color: "var(--text-3)" }}>No KPIs were scored (no scorecard configured).</p>
          )}
          {summary.kpis.map((k, idx) => (
            <div
              key={`${k.pillarId}-${k.kpiId}-${idx}`}
              style={{ padding: "12px 0", borderTop: idx ? "1px solid var(--border-1)" : "none" }}
            >
              <div
                style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
              >
                <div>
                  <div style={{ fontSize: 12, color: "var(--text-3)" }}>{k.pillarName}</div>
                  <div style={{ fontWeight: 600 }}>{k.kpiName}</div>
                </div>
                <select
                  style={{ ...input, color: GRADE_COLOR[grade(k)], fontWeight: 700 }}
                  value={grade(k)}
                  disabled={submitted || !canEdit}
                  onChange={(e) =>
                    update({
                      kpis: summary.kpis.map((x, i) =>
                        i === idx ? { ...x, editedGrade: e.target.value as KpiGrade } : x,
                      ),
                    })
                  }
                >
                  {GRADES.map((g) => (
                    <option key={g} value={g}>
                      {g}
                    </option>
                  ))}
                </select>
              </div>
              {k.evidence.length > 0 && (
                <ul style={{ margin: "8px 0 0", paddingLeft: 18 }}>
                  {k.evidence.map((e, i) => (
                    <li key={i} style={{ fontSize: 13, color: "var(--text-2)" }}>
                      “{e.quote}” {e.t && <span style={{ color: "var(--text-4)" }}>({e.t})</span>}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </section>

        {/* Questions asked */}
        <section style={card}>
          <h3 style={{ marginTop: 0 }}>Questions asked</h3>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {summary.questionsAsked.map((q, i) => (
              <li key={i} style={{ fontSize: 14, padding: "2px 0" }}>
                {q.text}{" "}
                {q.t && <span style={{ color: "var(--text-4)", fontSize: 12 }}>({q.t})</span>}
              </li>
            ))}
          </ul>
        </section>

        {/* Raw notes */}
        <section style={card}>
          <h3 style={{ marginTop: 0 }}>Raw notes</h3>
          {summary.rawNotes.map((n, i) => (
            <div key={i} style={{ fontSize: 14, padding: "3px 0" }}>
              <span
                style={{ color: "var(--text-4)", fontSize: 12, fontFamily: "var(--font-mono)" }}
              >
                {n.t}
              </span>{" "}
              {n.text}
            </div>
          ))}
        </section>
      </div>
    </Shell>
  );
}

const card: React.CSSProperties = {
  background: "var(--surface)",
  border: "1px solid var(--border-1)",
  borderRadius: 12,
  padding: 20,
  margin: "16px 0",
};
const input: React.CSSProperties = {
  padding: "8px 10px",
  borderRadius: 8,
  border: "1px solid var(--border-1)",
  background: "var(--surface-2)",
  color: "var(--text-1)",
  fontSize: 14,
};
